
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { LeadAccountWithProvider } from "@/hooks/admin/useAdminLeadAccounts";
import { fetchLeadAccounts, checkIsAdmin } from "@/api/leadAccounts";
import { supabase } from "@/integrations/supabase/client";
import { getUserProviderRole } from "@/api/leadAccounts";

export const useLeadAccountsLoader = () => {
  const [leadAccounts, setLeadAccounts] = useState<LeadAccountWithProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadLeadAccounts = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Error",
          description: "Not authenticated",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // First check if user is admin
      const isAdmin = await checkIsAdmin(session.user.id);
      
      // If user is not admin, check if they are a strategy provider
      let providerId: string | null = null;
      if (!isAdmin) {
        const roleData = await getUserProviderRole(session.user.id);
        
        if (!roleData || roleData.role !== 'strategy_provider') {
          // User is neither admin nor strategy provider, clear accounts and return
          setLeadAccounts([]);
          setLoading(false);
          return;
        }
        
        providerId = roleData.provider_id;
      }

      // Fetch lead accounts based on user role
      const data = await fetchLeadAccounts(isAdmin, providerId);
      setLeadAccounts(data);
    } catch (error) {
      console.error('Error loading lead accounts:', error);
      toast({
        title: "Error",
        description: "Failed to load lead accounts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    leadAccounts,
    loading,
    loadLeadAccounts
  };
};
